import { AlertTriangle, EyeOff, FileText, HardDrive, Lock, MessageSquare, UserCheck } from "lucide-react";
import ArrowButton from "../../components/ArrowButton/ArrowButton";
import LegalLayout, { LegalSection } from "./LegalLayout";

const LAW_URL = "https://www.planalto.gov.br/ccivil_03/_ato2015-2018/2018/lei/l13709.htm";

type Card = { Icon: typeof Lock; title: string; text: string };

const CONFIDENTIAL: Card[] = [
  {
    Icon: UserCheck,
    title: "Informações de clientes",
    text: "Dados pessoais, projetos, estratégias, resultados e qualquer informação que o cliente nos confia.",
  },
  {
    Icon: HardDrive,
    title: "Informações da empresa",
    text: "Dados financeiros, planos de negócios, estratégias de marketing, listas de clientes, informações de colaboradores e parceiros.",
  },
  {
    Icon: FileText,
    title: "Tecnologias e produtos",
    text: "Código-fonte, metodologias, algoritmos, segredos comerciais, especificações técnicas e qualquer know-how que desenvolvemos.",
  },
  {
    Icon: MessageSquare,
    title: "Informações verbais",
    text: "Conversas em reuniões, discussões sobre projetos ou qualquer outra informação sensível compartilhada verbalmente.",
  },
];

const RESPONSIBILITIES: Card[] = [
  {
    Icon: FileText,
    title: "Use as informações apenas para o trabalho",
    text: "Acesso e utilize dados confidenciais estritamente para cumprir suas responsabilidades na Calriz. Não use essas informações para benefício pessoal ou para qualquer outro fim.",
  },
  {
    Icon: EyeOff,
    title: "Seja discreto(a)",
    text: "Evite discutir projetos, informações de clientes ou dados internos em locais públicos, como cafés, restaurantes ou transporte.",
  },
  {
    Icon: Lock,
    title: "Mantenha a segurança",
    text: "Sempre bloqueie a tela do seu computador quando se ausentar e utilize senhas seguras. Não compartilhe suas credenciais de acesso com ninguém.",
  },
  {
    Icon: HardDrive,
    title: "Proteja os arquivos",
    text: "Não envie documentos ou dados confidenciais para e-mails pessoais. Utilize as ferramentas e plataformas corporativas designadas para o compartilhamento de informações.",
  },
  {
    Icon: UserCheck,
    title: "Fale com seu gestor(a)",
    text: "Se tiver dúvidas sobre a confidencialidade de uma informação, não arrisque. Consulte seu gestor(a) ou o time de Segurança da Informação.",
  },
  {
    Icon: MessageSquare,
    title: "Comunique problemas",
    text: "Se notar uma possível violação de segurança ou se uma informação confidencial for divulgada acidentalmente, notifique imediatamente seu gestor(a) para que possamos agir rapidamente.",
  },
];

function Cards({ items }: { items: Card[] }) {
  return (
    <ul className="cz-legal-cards">
      {items.map(({ Icon, title, text }) => (
        <li key={title} className="cz-legal-card">
          <span className="cz-legal-card__icon" aria-hidden="true">
            <Icon size={22} strokeWidth={1.8} />
          </span>
          <h3 className="cz-legal-card__title">{title}</h3>
          <p className="cz-legal-card__text">{text}</p>
        </li>
      ))}
    </ul>
  );
}

// /lgpd — página só em português (sem chaves de tradução), como era antes do redesign.
export default function LgpdPolicy() {
  const sections: LegalSection[] = [
    {
      id: "confidencialidade",
      title: "Nosso Compromisso com a Confidencialidade",
      content: (
        <>
          <p>Todas as informações da Calriz e de nossos clientes são consideradas confidenciais. Isso inclui, mas não se limita a:</p>
          <Cards items={CONFIDENTIAL} />
        </>
      ),
    },
    {
      id: "responsabilidades",
      title: "Nossas Responsabilidades no Dia a Dia",
      content: (
        <>
          <p>A proteção dessas informações é uma responsabilidade de todos. Para isso, esperamos que você:</p>
          <Cards items={RESPONSIBILITIES} />
        </>
      ),
    },
    {
      id: "violacao",
      title: "O que acontece em caso de violação?",
      content: (
        <div className="cz-legal-alert">
          <span className="cz-legal-alert__icon" aria-hidden="true">
            <AlertTriangle size={22} strokeWidth={1.9} />
          </span>
          <div>
            <p>
              A quebra desta política é uma falha grave, pois afeta diretamente a confiança dos nossos clientes e o futuro
              da Calriz. O descumprimento pode resultar em medidas disciplinares, que variam de advertência formal até o
              término do contrato de trabalho, além de possíveis ações legais.
            </p>
            <p>
              <strong>
                Acreditamos na nossa equipe e sabemos que juntos podemos proteger o que construímos. Nossa confiança mútua
                é a base de nossa cultura e de nosso sucesso.
              </strong>
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "lgpd",
      title: "A LGPD na Calriz",
      content: (
        <div className="cz-legal-dark">
          <span className="cz-shape cz-shape--drop cz-legal-dark__drop" aria-hidden="true" />
          <span className="cz-shape cz-shape--orb cz-legal-dark__orb" aria-hidden="true" />
          <p>
            A LGPD é a lei brasileira que protege as informações pessoais de todos. Para nós, isso significa que somos
            responsáveis por tratar os dados que gerenciamos com o máximo cuidado, garantindo a sua segurança, privacidade e
            integridade.
          </p>
          <p>
            A Calriz tem a segurança e a privacidade como pilares fundamentais de sua atuação. Por isso, declaramos que
            nossa operação e todas as nossas soluções estão em total conformidade e obediência à Lei Geral de Proteção de
            Dados (LGPD).
          </p>
          <p>
            Nosso compromisso com a LGPD vai além de uma obrigação legal; ele reflete a nossa cultura de confiança e
            transparência. Acreditamos que a inovação tecnológica deve caminhar lado a lado com a proteção rigorosa de
            dados.
          </p>
          <p>
            Isso significa que, em cada projeto e em todas as nossas atividades, garantimos a segurança, a privacidade e a
            integridade das informações de nossos clientes, parceiros e colaboradores, seguindo as melhores práticas para
            assegurar a tranquilidade de todos.
          </p>
          <ArrowButton href={LAW_URL} variant="white" size="md" icon="external">
            Conheça a Lei Geral de Proteção de Dados Pessoais - LGPD
          </ArrowButton>
        </div>
      ),
    },
  ];

  return <LegalLayout seo={{ page: "lgpd", path: "/lgpd" }} title="Confidencialidade e LGPD" icon={Lock} sections={sections} />;
}
